import { useEffect, useState } from "react";
import {
  EmptyState,
  Field,
  FieldGrid,
  Panel,
  PanelBody,
  PanelHead,
  TableWrap,
} from "../../components";
import { useAudit } from "../../api/system";
import { errorMessage, formatDateTime, formatInt } from "../../lib/format";
import { Pager } from "../../lib/Pager";

const PAGE_SIZE = 50;

/** Entity types the server writes audit rows for (see services using audit()). */
const ENTITY_TYPES = [
  "supplier",
  "supplier_branch",
  "brand",
  "brand_alias",
  "rate_card",
  "supplier_discount",
  "price_list",
  "price",
  "product",
  "comparable_group",
  "comparable_group_member",
  "project",
  "pricing",
  "pricing_line_item",
  "session",
];

export function ActivityPage() {
  const [entityType, setEntityType] = useState("");
  const [actorId, setActorId] = useState("");
  const [offset, setOffset] = useState(0);

  const audit = useAudit({
    ...(entityType ? { entityType } : {}),
    ...(actorId ? { actorId } : {}),
    limit: PAGE_SIZE,
    offset,
  });
  const items = audit.data?.items ?? [];

  // Actor options accumulate from every page seen, so the filter keeps working
  // after narrowing (there is no dedicated users endpoint).
  const [actors, setActors] = useState<Map<string, string>>(new Map());
  const auditData = audit.data;
  useEffect(() => {
    if (!auditData) return;
    setActors((current) => {
      let changed = false;
      const next = new Map(current);
      for (const entry of auditData.items) {
        if (entry.actorId && !next.has(entry.actorId)) {
          next.set(entry.actorId, entry.actorName ?? entry.actorId.slice(0, 8));
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [auditData]);

  return (
    <Panel>
      <PanelHead
        title="Activity"
        sub={
          audit.data
            ? `${formatInt(audit.data.total)} audited mutation${audit.data.total === 1 ? "" : "s"} — newest first`
            : "append-only audit log — who did what, when"
        }
      />
      <PanelBody>
        <FieldGrid>
          <Field label="Entity type">
            <select
              className="field"
              value={entityType}
              onChange={(event) => {
                setEntityType(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">All entity types</option>
              {ENTITY_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Actor">
            <select
              className="field"
              value={actorId}
              onChange={(event) => {
                setActorId(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">All actors</option>
              {[...actors.entries()].map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </Field>
        </FieldGrid>
      </PanelBody>
      {audit.isPending ? (
        <PanelBody>
          <EmptyState title="Loading activity…" />
        </PanelBody>
      ) : audit.isError ? (
        <PanelBody>
          <EmptyState title="Could not load activity" hint={errorMessage(audit.error)} />
        </PanelBody>
      ) : items.length === 0 ? (
        <PanelBody>
          <EmptyState title="No activity" hint="Mutations are logged here as they happen." />
        </PanelBody>
      ) : (
        <>
          <TableWrap>
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Entity type</th>
                <th>Entity</th>
              </tr>
            </thead>
            <tbody>
              {items.map((entry) => (
                <tr key={entry.id}>
                  <td className="mono">{formatDateTime(entry.at)}</td>
                  <td>
                    <strong>{entry.actorName ?? "System"}</strong>
                  </td>
                  <td className="mono">{entry.action}</td>
                  <td className="mono">{entry.entityType}</td>
                  <td className="mono" title={entry.entityId ?? undefined}>
                    {entry.entityId ? entry.entityId.slice(0, 8) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager total={audit.data.total} limit={PAGE_SIZE} offset={offset} onOffset={setOffset} />
        </>
      )}
    </Panel>
  );
}
